import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Typography, Grid } from '@mui/material';
import { Dialog, TextField, Snackbar } from 'daaas-ui';
import AxiosStatic from 'axios';
import SuccessDialog from './Success';
import { useAxios } from '../../Provider/AxiosProvider';
import { config } from '../../../Config/AppConfig';
import { validMaxLength, validRequired } from '../../../utils/validateString';
import {
  validKubeflowNamespaceName,
  validKubeflowNamespaceNameFirstChar,
  validKubeflowNamespaceNameLastChar,
} from '../../../utils/validateKubeflowNamespace';

export default function RequestKubeflowNamespace({
  open = false,
  toggleDialog,
}) {
  const { t } = useTranslation(['dialogs', 'common']);
  const axios = useAxios(true);

  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const statCanDomains = ['statcan.gc.ca'];
  const isStatCanAuthenticated =
    isAuthenticated &&
    instance.getActiveAccount() &&
    statCanDomains.indexOf(
      instance.getActiveAccount().username.split('@')[1].toLowerCase()
    ) !== -1;

  const dialogId = 'request-kubeflow-namespace';
  const [openSuccess, setOpenSuccess] = React.useState(false);
  const [openError, setOpenError] = React.useState(false);

  const initial = {
    numErrors: 0,
    dialogError: '',
    value: '',
    error: false,
    helperText: '',
  };
  const [enteredKubeFlowNamespace, setEnteredKubeFlowNamespace] = useState({
    ...initial,
  });

  const [submitted, setSubmitted] = useState(false);

  const kubeFlowNamespaceChangeHandler = (event) => {
    setEnteredKubeFlowNamespace({
      ...initial,
      numErrors: enteredKubeFlowNamespace.numErrors,
      value: event.target.value,
    });
  };

  const toggleSuccessDialog = () => {
    setOpenSuccess((prevOpenSuccess) => !prevOpenSuccess);
  };

  const closeBanner = () => {
    setEnteredKubeFlowNamespace({
      ...enteredKubeFlowNamespace,
      dialogError: '',
    });
  };

  const closeSnackbar = () => {
    setOpenError(false);
  };

  const clearForm = () => {
    setEnteredKubeFlowNamespace({ ...initial });
    toggleDialog();
  };

  const formValidation = () => {
    let numErrors = 0;
    const requiredMessage = t('requiredFieldError');
    if (!validRequired(enteredKubeFlowNamespace.value)) {
      numErrors += 1;
      setEnteredKubeFlowNamespace({
        ...enteredKubeFlowNamespace,
        numErrors,
        error: true,
        helperText: requiredMessage,
      });
    } else if (!validMaxLength(enteredKubeFlowNamespace.value, 240)) {
      numErrors += 1;
      setEnteredKubeFlowNamespace({
        ...enteredKubeFlowNamespace,
        numErrors,
        error: true,
        helperText: t('maxLengthField', { fieldLimit: 240 }),
      });
    } else if (!validKubeflowNamespaceName(enteredKubeFlowNamespace.value)) {
      numErrors += 1;
      setEnteredKubeFlowNamespace({
        ...enteredKubeFlowNamespace,
        numErrors,
        error: true,
        helperText: t('kubeflowRequestNameSpaceInvalidChars'),
      });
    } else if (
      !validKubeflowNamespaceNameFirstChar(enteredKubeFlowNamespace.value)
    ) {
      numErrors += 1;
      setEnteredKubeFlowNamespace({
        ...enteredKubeFlowNamespace,
        numErrors,
        error: true,
        helperText: t('kubeflowRequestNameSpaceInvalidFirstChar'),
      });
    } else if (
      !validKubeflowNamespaceNameLastChar(enteredKubeFlowNamespace.value)
    ) {
      numErrors += 1;
      setEnteredKubeFlowNamespace({
        ...enteredKubeFlowNamespace,
        numErrors,
        error: true,
        helperText: t('kubeflowRequestNameSpaceInvalidLastChar'),
      });
    }
    return numErrors > 0;
  };

  const focusFirstError = () => {
    document.getElementsByName('request-kubeflow-namespace-name')[0].focus();
  };

  // should fire up when send request button is clicked
  const handlePrimaryClick = () => {
    if (!formValidation()) {
      const data = {
        requested_namespace: enteredKubeFlowNamespace.value,
      };
      if (submitted) {
        return;
      }
      setSubmitted(true);
      axios
        .post(config.api.API_REQUEST_NAMESPACE, data)
        .then(() => {
          toggleDialog();
          toggleSuccessDialog();
          setEnteredKubeFlowNamespace({ ...initial });
        })
        .catch((error) => {
          const alertContainer = document.getElementById(`${dialogId}-alerts`);

          // If we have a cancellation error, it is due to missing authorization header.
          if (AxiosStatic.isCancel(error)) {
            setEnteredKubeFlowNamespace({
              ...enteredKubeFlowNamespace,
              dialogError: t('error401'),
            });
            alertContainer.scrollIntoView();
            return;
          }

          let status = 0;
          if ('response' in error) {
            if ('status' in error.response) {
              status = error.response.status;
            } else {
              status = error.response;
            }
          } else {
            status = 500;
            // eslint-disable-next-line no-console
            console.log(`error is ${error.toJson()}`);
          }

          switch (status) {
            case 400: {
              if (
                'display_errors' in error.response.data &&
                error.response.data.display_errors
              ) {
                setEnteredKubeFlowNamespace({
                  ...enteredKubeFlowNamespace,
                  error: true,
                  helperText: error.response.data.error_messages[0],
                });
              } else {
                setEnteredKubeFlowNamespace({
                  ...enteredKubeFlowNamespace,
                  dialogError: t('error400'),
                });
                alertContainer.scrollIntoView();
              }
              break;
            }
            case 401:
              setEnteredKubeFlowNamespace({
                ...enteredKubeFlowNamespace,
                dialogError: t('error401'),
              });
              alertContainer.scrollIntoView();
              break;
            case 429:
              setEnteredKubeFlowNamespace({
                ...enteredKubeFlowNamespace,
                dialogError: t('error429'),
              });
              alertContainer.scrollIntoView();
              break;
            case 500:
              setEnteredKubeFlowNamespace({
                ...enteredKubeFlowNamespace,
                dialogError: t('error500'),
              });
              alertContainer.scrollIntoView();
              break;
            default:
              // eslint-disable-next-line no-console
              console.log(`error is ${error.toJson()}`);
          }
        })
        .finally(() => {
          setSubmitted(false);
        });
    } else {
      setOpenError(true);
      focusFirstError();
    }
  };

  React.useEffect(() => {
    if (enteredKubeFlowNamespace.error) {
      focusFirstError();
    }
  }, [enteredKubeFlowNamespace.numErrors]);

  const errorMsg = () => {
    const errors = enteredKubeFlowNamespace.numErrors;
    if (errors === 1) {
      return `${errors} ${t('errorFound')}`;
    }
    return `${errors} ${t('errorsFound')}`;
  };

  const content = () => (
    <Grid container direction="column">
      <Grid
        item
        sx={(theme) => ({
          marginBottom: theme.spacing(3),
          color: alpha(theme.palette.common.black, 0.6),
        })}
      >
        <Typography variant="caption">{t('requiredFields')}</Typography>
      </Grid>
      <Grid item sx={{ mb: 3 }}>
        <Typography variant="body2">
          {t('kubeflowRequestNameSpaceAfterSubmit')}
        </Typography>
        {isStatCanAuthenticated ? (
          <>
            <br />
            <Typography variant="body2">
              {t('kubeflowRequestNameSpaceNameFormat')}
            </Typography>
          </>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
      </Grid>

      <Grid item>
        <TextField
          id="request-kubeflow-namespace-name"
          label={t('kubeflowRequestNameSpaceLabel')}
          multiline
          minRows="1"
          name="request-kubeflow-namespace-name"
          variant="outlined"
          value={enteredKubeFlowNamespace.value}
          onChange={kubeFlowNamespaceChangeHandler}
          error={enteredKubeFlowNamespace.error}
          helperText={enteredKubeFlowNamespace.helperText}
          fullWidth
          required
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Dialog
        id={dialogId}
        open={open}
        title={t('kubeflowRequestNameSpaceTitle')}
        primaryButton={t('kubeflowRequestNameSpaceSubmit')}
        secondaryButton={t('cancel')}
        handlePrimaryClick={handlePrimaryClick}
        handleSecondaryClick={clearForm}
        toggleDialog={clearForm}
        closeText={t('common:close')}
        onClose={clearForm}
        alertBanners={[
          {
            open: Boolean(enteredKubeFlowNamespace.dialogError),
            severity: 'error',
            message: enteredKubeFlowNamespace.dialogError,
            handleClose: closeBanner,
            closeText: t('closeErrorAlert'),
          },
        ]}
      >
        {content()}
      </Dialog>
      {/* Error snackbar */}
      <Snackbar
        message={errorMsg()}
        severity="error"
        open={openError}
        handleClose={closeSnackbar}
      />

      {/* Success dialog */}
      <SuccessDialog
        open={openSuccess}
        message={`${t('successMessage')} ${t('successWillContactMessage')}`}
        toggleDialog={toggleSuccessDialog}
      />
    </>
  );
}

RequestKubeflowNamespace.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
};
